import { useState, useEffect, useCallback } from 'react';
import { GOLD, EMBER, MUTE, MONO, BODY, GHOST } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Ic } from './shared';
import { usePlaidLink } from 'react-plaid-link';
import * as api from '../lib/api';

/* ═══════════════════════════════════════════════════════════════
   BANK CONNECTION SCREEN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

export function BankScreen({ onComplete, onSkip }) {
  const [phase, setPhase] = useState('intro');
  const [progress, setProgress] = useState(0);
  const [connectedBank, setConnectedBank] = useState('');
  const [linkToken, setLinkToken] = useState(null);

  // Fetch Plaid link token on mount
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plaid/create_link_token`, {
          method: 'POST',
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setLinkToken(data.link_token);
        }
      } catch (e) { console.error(e); }
    };
    fetchLinkToken();
  }, []);

  useEffect(() => {
    if (phase === 'connecting') {
      [20, 45, 70, 90, 100].forEach((p, i) => setTimeout(() => setProgress(p), (i + 1) * 600));
      setTimeout(() => setPhase('done'), 3500);
    }
  }, [phase]);

  // Plaid Link success handler
  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setPhase('connecting');
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plaid/exchange_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token: publicToken, metadata }),
        credentials: 'include'
      });
      setConnectedBank(metadata?.institution?.name || 'Bank');
    } catch (e) { console.error(e); }
  }, []);

  // Initialize Plaid Link
  const isOAuthRedirect = window.location.search.includes('oauth_state_id');
  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: () => {},
    receivedRedirectUri: isOAuthRedirect ? window.location.href : undefined,
  });

  // Auto-resume Plaid when returning from OAuth redirect
  useEffect(() => {
    if (isOAuthRedirect && plaidReady) {
      openPlaidLink();
      window.history.replaceState({}, '', '/');
    }
  }, [isOAuthRedirect, plaidReady, openPlaidLink]);

  if (phase === 'intro') return (
    <div data-testid="bank-intro" className="dettio-onboarding--intro" style={{ position: 'relative' }}>
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner dettio-screen__center">
        <div style={{ marginBottom: 20 }}>{Ic.bank(48, GOLD)}</div>
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>Connect your bank</Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 28 }}>
          Securely link your accounts to auto-track payments, collect round-ups, and analyze spending.
        </Bd>
        <div className="dettio-check-list">
          {[
            { icon: Ic.check, text: 'Auto-log payments as they happen' },
            { icon: Ic.check, text: 'Round-up spare change to debt' },
            { icon: Ic.check, text: 'Track spending patterns' },
            { icon: Ic.check, text: 'Connect Affirm, Klarna & BNPL' },
          ].map((item, i) => (
            <div key={i} className="dettio-check-item">
              {item.icon(14, GOLD)}
              <Bd style={{ fontSize: 13, color: 'var(--light)' }}>{item.text}</Bd>
            </div>
          ))}
        </div>
        <Btn onClick={() => plaidReady && openPlaidLink()} gold disabled={!plaidReady} data-testid="connect-bank-btn">
          {!plaidReady ? 'LOADING...' : 'CONNECT YOUR BANK'}
        </Btn>
        <button onClick={onSkip} data-testid="skip-bank-btn" className="dettio-btn-text" style={{ marginTop: 12 }}>
          SKIP — I'LL ENTER MANUALLY
        </button>
        <Mo style={{ fontSize: 9, color: 'var(--ghost)', display: 'block', marginTop: 16, lineHeight: 1.6 }}>
          Powered by Plaid. 11,000+ banks supported. Bank-grade encryption. Read-only access.
        </Mo>
      </div>
    </div>
  );

  if (phase === 'connecting') return (
    <div data-testid="bank-connecting" className="dettio-connecting">
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <div className="pulse-a" style={{ marginBottom: 24 }}>{Ic.bank(48, GOLD)}</div>
        <Dp style={{ fontSize: 18, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 6 }}>Syncing your accounts...</Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 28 }}>Fetching accounts and transactions</Bd>
        <div className="dettio-connecting__progress">
          <div className="dettio-connecting__bar" style={{ width: `${progress}%` }} />
        </div>
        {['Authenticating connection', 'Fetching accounts', 'Syncing balances', 'Loading transactions'].map((s, i) => (
          <div key={i} className="dettio-connecting__step" style={{ color: progress >= (i + 1) * 25 ? MUTE : GHOST, fontFamily: BODY }}>
            <Mo style={{ fontSize: 10, color: progress >= (i + 1) * 25 ? GOLD : GHOST }}>{progress >= (i + 1) * 25 ? '\u2713' : '\u25CB'}</Mo>{s}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="bank-done" style={{ padding: '60px 28px', position: 'relative' }} className="dettio-screen__center">
      <Horizon intensity={0.8} />
      <div className="dettio-screen__inner">
        <div style={{ marginBottom: 24 }}>{Ic.check(48, GOLD)}</div>
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>Connected{connectedBank ? ` to ${connectedBank}` : ''}</Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block', marginBottom: 28 }}>Auto-sync, round-ups, and spending analysis are now active.</Bd>
        <Btn onClick={onComplete} gold data-testid="continue-btn">CONTINUE TO DETTIO</Btn>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ONBOARDING SCREEN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

export function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    display_name: '',
    emotional_state: '',
    debt_awareness: '',
    primary_goal: '',
    household_size: 1,
    hidden_debts: '',
    motivation_style: '',
    pay_frequency: '',
  });

  const updateAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = () => setStep(prev => prev + 1);
  
  const handleSaveProfile = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      // Mark onboarded BEFORE Plaid launches so OAuth redirects don't loop back to onboarding
      await api.completeOnboarding();
    } catch (err) {
      console.error('Failed to save onboarding profile:', err);
    }
    handleNext();
  };

  const handleBankComplete = async () => {
    try {
      await api.completeOnboarding();
    } catch (e) {
      console.error(e);
    }
    onComplete();
  };

  // Progress dots
  const ProgressDots = ({ current, total }) => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: i === current ? GOLD : 'var(--faint)',
            transition: 'all 0.3s ease',
          }}
        />
      ))}
    </div>
  );

  // Option button component (reusable)
  const OptionButton = ({ label, value, selected, onClick, emoji }) => (
    <button
      onClick={() => onClick(value)}
      style={{
        width: '100%',
        padding: '18px 20px',
        background: selected ? `${GOLD}15` : 'var(--surface)',
        border: selected ? `2px solid ${GOLD}` : '2px solid var(--faint)',
        borderRadius: 12,
        cursor: 'pointer',
        marginBottom: 12,
        transition: 'all 0.2s ease',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {emoji && <span style={{ fontSize: 24 }}>{emoji}</span>}
      <Bd style={{ fontSize: 14, fontWeight: 600, color: selected ? GOLD : 'var(--light)', flex: 1 }}>
        {label}
      </Bd>
      {selected && (
        <div style={{ width: 20, height: 20, borderRadius: '50%', background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Mo style={{ fontSize: 12, color: '#000' }}>✓</Mo>
        </div>
      )}
    </button>
  );

  const steps = [
    // Step 0: Welcome
    () => (
      <div className="dettio-screen__center">
        <div style={{ marginBottom: 20 }}>{Ic.flame(48, GOLD)}</div>
        <Dp style={{ fontSize: 30, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -1, marginBottom: 10 }}>You're not alone</Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 28 }}>
          80% of Americans carry debt. The system is designed to keep you there — minimum payments, hidden interest, lifestyle creep.
        </Bd>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 28 }}>
          Dettio is different. No motivational fluff. No vague budgeting advice. Just math, psychology, and a clear path out.
        </Bd>
      </div>
    ),

    // Step 1: Name
    () => (
      <div>
        <ProgressDots current={0} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          What should we call you?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          We'll use this to personalize your experience.
        </Bd>
        <input
          type="text"
          data-testid="name-input"
          placeholder="Your first name"
          value={answers.display_name}
          onChange={(e) => updateAnswer('display_name', e.target.value)}
          style={{
            width: '100%',
            padding: '16px 18px',
            background: 'var(--surface)',
            border: `2px solid ${answers.display_name ? GOLD : 'var(--faint)'}`,
            borderRadius: 12,
            color: 'var(--light)',
            fontSize: 16,
            fontFamily: BODY,
            marginBottom: 24,
          }}
          autoFocus
        />
      </div>
    ),

    // Step 2: Emotional State
    () => (
      <div>
        <ProgressDots current={1} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          How would you describe your debt situation right now?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          Be honest — this helps us give you the right support.
        </Bd>
        <OptionButton label="I feel like I'm drowning" value="drowning" selected={answers.emotional_state === 'drowning'} onClick={(v) => updateAnswer('emotional_state', v)} emoji="😰" />
        <OptionButton label="I'm managing, but barely" value="managing" selected={answers.emotional_state === 'managing'} onClick={(v) => updateAnswer('emotional_state', v)} emoji="😅" />
        <OptionButton label="I'm not sure where I stand" value="unsure" selected={answers.emotional_state === 'unsure'} onClick={(v) => updateAnswer('emotional_state', v)} emoji="🤔" />
        <OptionButton label="I'm ready to make a plan" value="ready" selected={answers.emotional_state === 'ready'} onClick={(v) => updateAnswer('emotional_state', v)} emoji="💪" />
      </div>
    ),

    // Step 3: Debt Awareness
    () => (
      <div>
        <ProgressDots current={2} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          Do you know roughly how much you owe in total?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          No judgment — many people avoid looking at the numbers.
        </Bd>
        <OptionButton label="Yes, I know the exact number" value="exact" selected={answers.debt_awareness === 'exact'} onClick={(v) => updateAnswer('debt_awareness', v)} emoji="📊" />
        <OptionButton label="I have a general idea" value="general" selected={answers.debt_awareness === 'general'} onClick={(v) => updateAnswer('debt_awareness', v)} emoji="💭" />
        <OptionButton label="I've been avoiding looking" value="avoiding" selected={answers.debt_awareness === 'avoiding'} onClick={(v) => updateAnswer('debt_awareness', v)} emoji="🙈" />
        <OptionButton label="No clue, honestly" value="no_clue" selected={answers.debt_awareness === 'no_clue'} onClick={(v) => updateAnswer('debt_awareness', v)} emoji="🤷" />
      </div>
    ),

    // Step 4: Primary Goal
    () => (
      <div>
        <ProgressDots current={3} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          What matters most to you right now?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          Choose what resonates — we'll help you get there.
        </Bd>
        <OptionButton label="Pay everything off as fast as possible" value="pay_fast" selected={answers.primary_goal === 'pay_fast'} onClick={(v) => updateAnswer('primary_goal', v)} emoji="⚡" />
        <OptionButton label="Stop living paycheck to paycheck" value="stop_paycheck" selected={answers.primary_goal === 'stop_paycheck'} onClick={(v) => updateAnswer('primary_goal', v)} emoji="🔄" />
        <OptionButton label="Get my spending under control" value="control_spending" selected={answers.primary_goal === 'control_spending'} onClick={(v) => updateAnswer('primary_goal', v)} emoji="🎯" />
        <OptionButton label="Build a real plan I can follow" value="build_plan" selected={answers.primary_goal === 'build_plan'} onClick={(v) => updateAnswer('primary_goal', v)} emoji="📋" />
        <OptionButton label="Just understand where my money goes" value="understand" selected={answers.primary_goal === 'understand'} onClick={(v) => updateAnswer('primary_goal', v)} emoji="🔍" />
      </div>
    ),

    // Step 5: Household
    () => (
      <div>
        <ProgressDots current={4} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          How many people depend on your income?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          This helps us understand your financial pressure.
        </Bd>
        <OptionButton label="Just me" value={1} selected={answers.household_size === 1} onClick={(v) => updateAnswer('household_size', v)} emoji="👤" />
        <OptionButton label="1 other person" value={2} selected={answers.household_size === 2} onClick={(v) => updateAnswer('household_size', v)} emoji="👥" />
        <OptionButton label="2-3 others" value={3} selected={answers.household_size === 3} onClick={(v) => updateAnswer('household_size', v)} emoji="👨‍👩‍👧" />
        <OptionButton label="4 or more" value={5} selected={answers.household_size === 5} onClick={(v) => updateAnswer('household_size', v)} emoji="👨‍👩‍👧‍👦" />
      </div>
    ),

    // Step 6: Hidden Debts
    () => (
      <div>
        <ProgressDots current={5} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          Do you have any debts that won't show up in your bank transactions?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          Payday loans, cash advances, family loans, etc.
        </Bd>
        <OptionButton label="No, everything goes through my bank" value="none" selected={answers.hidden_debts === 'none'} onClick={(v) => updateAnswer('hidden_debts', v)} emoji="✅" />
        <OptionButton label="Yes, I'll add them manually" value="has_hidden" selected={answers.hidden_debts === 'has_hidden'} onClick={(v) => updateAnswer('hidden_debts', v)} emoji="📝" />
        <OptionButton label="I'm not sure" value="unsure" selected={answers.hidden_debts === 'unsure'} onClick={(v) => updateAnswer('hidden_debts', v)} emoji="❓" />
      </div>
    ),

    // Step 7: Motivation Style
    () => (
      <div>
        <ProgressDots current={6} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          What helps you stay on track?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          We'll personalize your dashboard to match.
        </Bd>
        <OptionButton label="Show me the numbers — data motivates me" value="numbers" selected={answers.motivation_style === 'numbers'} onClick={(v) => updateAnswer('motivation_style', v)} emoji="📈" />
        <OptionButton label="Give me a step-by-step plan to follow" value="steps" selected={answers.motivation_style === 'steps'} onClick={(v) => updateAnswer('motivation_style', v)} emoji="📋" />
        <OptionButton label="Help me understand WHY I'm in this pattern" value="understanding" selected={answers.motivation_style === 'understanding'} onClick={(v) => updateAnswer('motivation_style', v)} emoji="💡" />
        <OptionButton label="Just tell me when I'll be debt-free" value="payoff_date" selected={answers.motivation_style === 'payoff_date'} onClick={(v) => updateAnswer('motivation_style', v)} emoji="🎯" />
      </div>
    ),

    // Step 8: Pay Frequency
    () => (
      <div>
        <ProgressDots current={7} total={8} />
        <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          How often do you get paid?
        </Dp>
        <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 24 }}>
          This helps us time your payment reminders.
        </Bd>
        <OptionButton label="Every week" value="weekly" selected={answers.pay_frequency === 'weekly'} onClick={(v) => updateAnswer('pay_frequency', v)} emoji="📅" />
        <OptionButton label="Every two weeks" value="biweekly" selected={answers.pay_frequency === 'biweekly'} onClick={(v) => updateAnswer('pay_frequency', v)} emoji="🗓️" />
        <OptionButton label="Twice a month (1st and 15th)" value="semimonthly" selected={answers.pay_frequency === 'semimonthly'} onClick={(v) => updateAnswer('pay_frequency', v)} emoji="📆" />
        <OptionButton label="Once a month" value="monthly" selected={answers.pay_frequency === 'monthly'} onClick={(v) => updateAnswer('pay_frequency', v)} emoji="🗒️" />
        <OptionButton label="It varies — I'm self-employed" value="irregular" selected={answers.pay_frequency === 'irregular'} onClick={(v) => updateAnswer('pay_frequency', v)} emoji="💼" />
      </div>
    ),

    // Step 9: Transition to Bank Connect
    () => (
      <div className="dettio-screen__center">
        <div style={{ marginBottom: 20 }}>{Ic.bank(48, GOLD)}</div>
        <Dp style={{ fontSize: 26, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
          Perfect. Now let's connect your bank.
        </Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 16 }}>
          We'll securely sync your accounts so we can see the full picture and give you accurate advice.
        </Bd>
        <div className="dettio-info-box dettio-info-box--gold" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div>{Ic.lock(18, GOLD)}</div>
            <div>
              <Bd style={{ fontSize: 12, color: GOLD, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Bank-grade security
              </Bd>
              <Mo style={{ fontSize: 11, color: GOLD, lineHeight: 1.6 }}>
                Powered by Plaid. 11,000+ banks supported. Read-only access. We never see your login credentials.
              </Mo>
            </div>
          </div>
        </div>
      </div>
    ),

    // Step 10: Bank Connection
    () => <BankScreen onComplete={handleBankComplete} onSkip={handleBankComplete} />,
  ];

  const canProceed = () => {
    if (step === 0) return true; // Welcome
    if (step === 1) return answers.display_name.trim().length > 0;
    if (step === 2) return answers.emotional_state !== '';
    if (step === 3) return answers.debt_awareness !== '';
    if (step === 4) return answers.primary_goal !== '';
    if (step === 5) return answers.household_size > 0;
    if (step === 6) return answers.hidden_debts !== '';
    if (step === 7) return answers.motivation_style !== '';
    if (step === 8) return answers.pay_frequency !== '';
    if (step === 9) return true; // Transition screen
    return true;
  };

  const handleContinue = () => {
    if (step === 8) {
      // Save profile before bank connection
      handleSaveProfile();
    } else {
      handleNext();
    }
  };

  return (
    <div data-testid="onboarding" style={{ padding: step === 10 ? '0' : '60px 28px', minHeight: '100vh', position: 'relative' }}>
      <Horizon intensity={0.8} />
      <div className="dettio-screen__inner" style={{ maxWidth: 480, margin: '0 auto' }}>
        {steps[step]()}
        {step < 10 && (
          <Btn 
            onClick={handleContinue} 
            gold 
            disabled={!canProceed()}
            data-testid={`onboarding-next-${step}`}
            style={{ marginTop: 28 }}
          >
            {step === 0 ? "LET'S GET STARTED" : step === 9 ? "CONNECT YOUR BANK" : "CONTINUE"}
          </Btn>
        )}
        {step > 0 && step < 10 && (
          <button 
            onClick={() => setStep(prev => prev - 1)} 
            className="dettio-btn-text" 
            style={{ marginTop: 12 }}
          >
            BACK
          </button>
        )}
      </div>
    </div>
  );
}
